import { useState, useContext } from "react";
import AuthContext from "../../../store/auth-context";
import { useNavigate } from "react-router-dom";
import { getAuthToken } from "../../../util/auth";
import { getApiChara } from "../../../util/getApi";
import { toast } from "react-toastify";
import Loader from "../../UI/Loader";
import { Table, Button, Modal, Form, Row, Col, Badge, Spinner } from "react-bootstrap";
import { FaSearch, FaHistory, FaPlus, FaTrash, FaEdit, FaPrint, FaFilePdf, FaCheck, FaTimes, FaMoneyBillWave, FaList, FaWhatsapp } from 'react-icons/fa';
import { printBillStatementReceipt, exportBillPDF, shareBillViaWhatsApp } from "./BillStatementReceipt";
import ConfirmModal from "../../UI/ConfirmModal";
import { useTranslation } from "react-i18next";
const BillingConfig = () => {
  const { t } = useTranslation();
  const authCtx = useContext(AuthContext);
  const hospitalData = authCtx.hospitalData;
  const navigate = useNavigate();
  const [searchPara, setSearchPara] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [patientData, setPatientData] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientBills, setPatientBills] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateBill, setUpdateBill] = useState(null);

  // Confirmation States
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteBillId, setDeleteBillId] = useState(null);
  const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);

  const handleSearchChange = (e) => setSearchPara(e.target.value);

  // ✅ Fetch patient by name or mobile
  const onSearchPatientHandler = async (e) => {
    e.preventDefault();

    const token = getAuthToken();
    if (!token || token === "EXPIRED") {
      navigate("/");
      return;
    }

    setIsLoading(true);
    try {
      const apiChar = getApiChara();
      const apiUrl =
        `${apiChar}/patient/getPatientByFNameOrLNameOrMobile?value=` +
        searchPara;

      const response = await fetch(apiUrl, {
        headers: { Authorization: "Bearer " + token },
      });
      if (!response.ok) throw new Error("Failed to fetch patient data");

      const data = await response.json();
      if (!data || data.length === 0) {
        toast.info(t("No patients found"));
      }
      setPatientData(data);
    } catch (err) {
      console.error("Search error:", err);
      toast.error(t("Error fetching patients"));
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Select patient & fetch their bills
  const getSelectedPatientFromList = async (patient) => {
    setSelectedPatient(patient);
    setPatientData([]);

    if (!patient?.patientId) return;

    const token = getAuthToken();
    if (!token || token === "EXPIRED") {
      navigate("/");
      return;
    }

    try {
      setIsLoading(true);
      const apiChar = getApiChara();
      const apiUrl = `${apiChar}/billings/patient/${patient.patientId}`;
      const response = await fetch(apiUrl, {
        headers: { Authorization: "Bearer " + token },
      });
      if (!response.ok) throw new Error("Failed to fetch billing data");

      const data = await response.json();
      setPatientBills(data || []);
    } catch (err) {
      console.error("Billing fetch error:", err);
      toast.error(t("Error fetching billing data"));
    } finally {
      setIsLoading(false);
    }
  };

  const onDataCloseHandler = () => setPatientData([]);

  const handleShowServices = (services) => {
    setSelectedServices(services);
    console.log(services, "services");
    setShowServiceModal(true);
  };

  // ✅ DELETE Billing
  // ✅ DELETE Billing (Trigger Modal)
  const handleDeleteBill = (billId) => {
    setDeleteBillId(billId);
    setShowDeleteConfirm(true);
  };

  // Actual Delete
  const confirmDeleteBill = async () => {
    if (!deleteBillId) return;

    const token = getAuthToken();
    if (!token || token === "EXPIRED") {
      navigate("/");
      return;
    }

    try {
      setIsLoading(true);
      const apiChar = getApiChara();
      const apiUrl = `${apiChar}/billings/${deleteBillId}`;
      const response = await fetch(apiUrl, {
        method: "DELETE",
        headers: { Authorization: "Bearer " + token },
      });

      if (!response.ok) throw new Error("Failed to delete bill");

      toast.success(t("Bill deleted successfully"));
      // Refresh bills
      getSelectedPatientFromList(selectedPatient);
    } catch (err) {
      console.error("Delete error:", err);
      toast.error(t("Error deleting bill"));
    } finally {
      setIsLoading(false);
      setShowDeleteConfirm(false);
      setDeleteBillId(null);
    }
  };

  // ✅ UPDATE Billing (show modal)
  const handleUpdateBill = (bill) => {
    setUpdateBill({
      ...bill,
      serviceItems: bill.serviceItems.map((i) => ({
        ...i,
        quantity: i.quantity === 0 ? "" : i.quantity || "",
        actualAmount: i.actualAmount === 0 ? "" : i.actualAmount || "",
        totalAmount: (Number(i.actualAmount) * Number(i.quantity) || 0).toFixed(2),
        discount: i.discount === 0 ? "" : i.discount || "",
      })),
    });

    setShowUpdateModal(true);
  };

  const handleServiceItemChange = (index, field, value) => {
    const newItems = [...updateBill.serviceItems];
    const it = { ...newItems[index] };

    // Description should remain text
    if (field === "serviceDescription") {
      it[field] = value; // keep string as-is
    } else {
      // For number fields
      let v = String(value);

      // Remove leading zeros
      v = v.replace(/^0+(?=\d)/, "");

      // Empty allowed
      it[field] = v === "" ? "" : Number(v);
    }

    // Calculate values using numbers (empty → treat as 0)
    const qty = Number(it.quantity) || 0;
    const amt = Number(it.actualAmount) || 0;
    const dis = Number(it.discount) || 0;

    const gross = qty * amt;
    const discVal = (gross * dis) / 100;
    const net = gross - discVal;

    it.totalAmount = Number(gross.toFixed(2));
    it.netAmount = Number(net.toFixed(2));
    newItems[index] = it;

    const total = newItems.reduce(
      (sum, s) => sum + (Number(s.netAmount) || 0),
      0
    );

    const out = total - (Number(updateBill.amountReceived) || 0);

    setUpdateBill({
      ...updateBill,
      serviceItems: newItems,
      totalAmount: Number(total.toFixed(2)),
      amountReceived: Number(total.toFixed(2)),
      outstandingAmount: 0,
    });
  };



  const handleAddServiceItem = () => {
    const last = updateBill.serviceItems[updateBill.serviceItems.length - 1];

    if (!last.serviceDescription) {
      toast.warning(t("Please enter expense name before adding new one."));
      return;
    }
    if (last.quantity === "" || Number(last.quantity) <= 0) {
      toast.warning(t("Please enter quantity greater than 0."));
      return;
    }
    if (last.actualAmount === "" || Number(last.actualAmount) <= 0) {
      toast.warning(t("Please enter valid amount."));
      return;
    }

    const newItem = {
      serviceDescription: "",
      quantity: 1,
      actualAmount: "",
      totalAmount: "",
      discount: 0,
      netAmount: 0,
    };

    setUpdateBill({
      ...updateBill,
      serviceItems: [...updateBill.serviceItems, newItem],

      // ✅ CLEAR AMOUNT RECEIVED WHEN ADDING NEW ROW
      amountReceived: Number(updateBill.totalAmount.toFixed(2)),

      // Recalculate outstanding
      // outstandingAmount: updateBill.totalAmount,
      outstandingAmount: 0,
    });
  };




  const handleDeleteServiceItem = (index) => {
    if (updateBill.serviceItems.length === 1) {
      toast.error(t("At least one expense must remain."));
      return;
    }

    const newItems = [...updateBill.serviceItems];
    newItems.splice(index, 1);

    const total = newItems.reduce(
      (sum, s) => sum + (Number(s.netAmount) || 0),
      0
    );
    const out = total - (Number(updateBill.amountReceived) || 0);

    setUpdateBill({
      ...updateBill,
      serviceItems: newItems,
      totalAmount: Number(total.toFixed(2)),
      amountReceived: Number(total.toFixed(2)),

      // ⭐ outstanding becomes 0
      outstandingAmount: 0,
    });
  };


  // ✅ Save Updated Bill
  // ✅ Trigger Update Confirmation
  const handleSaveUpdateClick = () => {
    // No patient selected
    if (!selectedPatient?.patientId) {
      toast.warning(t("Please select a patient."));
      return;
    }

    // Service items required
    if (!updateBill.serviceItems || updateBill.serviceItems.length === 0) {
      toast.warning(t("Please add at least one expense."));
      return;
    }

    // Validate each service row
    for (const [i, s] of updateBill.serviceItems.entries()) {
      if (!s.serviceDescription) {
        toast.warning(t("Expense name missing (row {{row}}).", { row: i + 1 }));
        return;
      }
      if (s.actualAmount === "" || isNaN(s.actualAmount) || Number(s.actualAmount) <= 0) {
        toast.warning(t("Invalid amount in row {{row}}.", { row: i + 1 }));
        return;
      }
      if (s.quantity === "" || isNaN(s.quantity) || Number(s.quantity) <= 0) {
        toast.warning(t("Quantity must be greater than 0 (row {{row}}).", { row: i + 1 }));
        return;
      }
    }

    // Validate amount received
    if (
      updateBill.amountReceived === "" ||
      isNaN(updateBill.amountReceived) ||
      Number(updateBill.amountReceived) < 0
    ) {
      toast.warning(t("Please enter valid amount received."));
      return;
    }

    setShowUpdateConfirm(true);
  };

  // Actual Update API Call
  const confirmUpdateBill = async () => {
    const token = getAuthToken();
    if (!token || token === "EXPIRED") {
      navigate("/");
      return;
    }

    try {
      setIsLoading(true);
      const apiChar = getApiChara();
      const apiUrl = `${apiChar}/billings/${updateBill.id}`;

      const formatted = updateBill.billingDate
        ? new Date(updateBill.billingDate).toISOString().slice(0, 19).replace("T", " ")
        : null;

      const sendBill = {
        ...updateBill,
        billingDate: formatted,
        serviceItems: updateBill.serviceItems.map((i) => ({
          serviceDescription: i.serviceDescription,
          quantity: Number(i.quantity) || 1,
          actualAmount: Number(i.actualAmount) || 0,
          totalAmount: (Number(i.actualAmount) * Number(i.quantity)).toFixed(2),
          discount: Number(i.discount) || 0,
          netAmount: Number(i.netAmount) || 0,
        })),
      };

      const response = await fetch(apiUrl, {
        method: "PUT",
        headers: {
          Authorization: "Bearer " + token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(sendBill),
      });

      if (!response.ok) throw new Error();

      toast.success(t("Updated successfully"));
      setShowUpdateModal(false);
      getSelectedPatientFromList(selectedPatient);
    } catch {
      toast.error(t("Update failed"));
    } finally {
      setIsLoading(false);
      setShowUpdateConfirm(false);
    }
  };


  // ✅ Totals
  const totalAmount = patientBills.reduce(
    (sum, b) => sum + (b.totalAmount || 0),
    0
  );
  const totalReceived = patientBills.reduce(
    (sum, b) => sum + (b.amountReceived || 0),
    0
  );
  const totalOutstanding = patientBills.reduce(
    (sum, b) => sum + (b.outstandingAmount || 0),
    0
  );

  return (
    <div className="container-fluid px-2 px-md-4 pt-3" style={{ overflowX: 'hidden' }}>
      {isLoading && <Loader />}

      <form onSubmit={onSearchPatientHandler} className="mb-3 px-1 w-100">
        <div className="d-flex align-items-center w-100">
          <label className="col-form-label fw-bold small text-nowrap d-none d-md-block me-2">{t("Search Patient")}</label>
          <div className="input-group flex-grow-1">
            <input
              type="text"
              className="form-control bordergrey"
              placeholder={t("Search by Name or Mobile")}
              value={searchPara}
              onChange={handleSearchChange}
              required
            />
            <button type="submit" className="btn button">
              <FaSearch />
            </button>
          </div>
        </div>
      </form>

      {/* Selected Patient Info */}
      {selectedPatient && (
        <div className="alert alert-info d-flex justify-content-between align-items-center">
          <div>
            <strong>{t("Patient Id")}:</strong> {selectedPatient.patientId}
          </div>
          <div>
            <strong>{t("Name")}:</strong>{" "}
            {[
              selectedPatient.firstName,
              selectedPatient.middleName,
              selectedPatient.lastName,
            ]
              .filter(Boolean)
              .join(" ")}
          </div>
        </div>
      )}

      {/* Bills Table */}
      {patientBills.length > 0 && (
        <div className="mt-4 table-responsive border rounded shadow-sm">
          <style>
            {`
              @media (max-width: 768px) {
                .table-responsive table {
                  font-size: 11px !important;
                }
                .table-responsive th, .table-responsive td {
                  padding: 8px 4px !important;
                }
                .btn-icon-group {
                  display: flex !important;
                  gap: 2px !important;
                }
              }
            `}
          </style>
          <Table striped bordered hover size="sm" className="mb-0">
            <thead>
              <tr>
                <th>{t("Bill ID")}</th>
                <th>{t("Date")}</th>
                <th>{t("Net Amount")}</th>
                <th>{t("Amount Received")}</th>
                <th>{t("Outstanding")}</th>
                <th>{t("Expenses Info")}</th>
                <th>{t("Action")}</th>
              </tr>
            </thead>
            <tbody>
              {patientBills.map((bill) => (
                <tr key={bill.id}>
                  <td>{bill.id}</td>
                  <td>{bill.billingDate}</td>
                  <td>{bill.totalAmount}</td>
                  <td>{bill.amountReceived}</td>
                  <td>{bill.outstandingAmount}</td>
                  <td className="text-center">
                    {bill.serviceItems && bill.serviceItems.length > 0 ? (
                      <Button
                        className="btn-outline-theme"
                        size="sm"
                        onClick={() => handleShowServices(bill.serviceItems)}
                      >
                        <i className="fa fa-plus"></i> {t("View")}
                      </Button>
                    ) : (
                      t("No Expenses Added")
                    )}
                  </td>
                  <td className="text-center text-nowrap">
                    <div className="d-inline-flex gap-1 btn-icon-group">
                      <Button
                        variant="link"
                        className="p-1 px-2 text-success"
                        onClick={() => handleUpdateBill(bill)}
                        title={t("Edit")}
                      >
                        <FaEdit />
                      </Button>
                      <Button
                        variant="link"
                        className="p-1 px-2 text-danger"
                        onClick={() => handleDeleteBill(bill.id)}
                        title={t("Delete")}
                      >
                        <FaTrash />
                      </Button>
                      <Button
                        variant="link"
                        className="p-1 px-2 text-secondary"
                        onClick={() =>
                          printBillStatementReceipt(
                            selectedPatient,
                            bill.serviceItems || [],
                            bill.totalAmount,
                            bill.amountReceived,
                            bill.outstandingAmount,
                            bill.billingDate,
                            bill.paymentType,
                            bill.txnNo || null,
                            hospitalData,
                            bill.id
                          )
                        }
                        title={t("Print")}
                      >
                        <FaPrint />
                      </Button>
                      <Button
                        variant="link"
                        className="p-1 px-2 text-info"
                        onClick={() =>
                          exportBillPDF(
                            selectedPatient,
                            bill.serviceItems || [],
                            bill.totalAmount,
                            bill.amountReceived,
                            bill.outstandingAmount,
                            bill.billingDate,
                            bill.paymentType,
                            bill.txnNo || null,
                            "a4",
                            hospitalData,
                            bill.id
                          )
                        }
                        title={t("Export")}
                      >
                        <FaFilePdf />
                      </Button>
                      <Button
                        variant="link"
                        className="p-1 px-2"
                        style={{ color: '#25D366' }}
                        onClick={() =>
                          shareBillViaWhatsApp(
                            selectedPatient,
                            bill.serviceItems || [],
                            bill.totalAmount,
                            bill.amountReceived,
                            bill.outstandingAmount,
                            bill.billingDate,
                            bill.paymentType,
                            bill.txnNo || null,
                            hospitalData,
                            bill.id
                          )
                        }
                        title={t("WhatsApp")}
                      >
                        <FaWhatsapp style={{ fontSize: '1.1rem' }} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}

              {/* Totals Row */}
              <tr className="fw-bold bg-light">
                <td colSpan={2} className="text-end">
                  {t("Total")}
                </td>
                <td>{totalAmount.toFixed(2)}</td>
                <td>{totalReceived.toFixed(2)}</td>
                <td>{totalOutstanding.toFixed(2)}</td>
                <td colSpan={2}></td>
              </tr>
            </tbody>
          </Table>
        </div>
      )}

      {/* Patient Selection Modal */}
      <Modal
        show={patientData?.length > 0}
        onHide={onDataCloseHandler}
        centered
        size="lg"
        className="patientModel"
      >
        <Modal.Header
          closeButton
          style={{
            backgroundColor: "var(--primary-color)",
            color: "white",
            border: "none",
          }}
        >
          <Modal.Title className="w-100 text-center text-white" style={{ color: "white" }}>{t("Select Patient")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="table-responsive">
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>{t("Patient Id")}</th>
                  <th>{t("Name")}</th>
                  <th>{t("Mobile")}</th>
                  <th>{t("Action")}</th>
                </tr>
              </thead>
              <tbody>
                {patientData.map((p) => (
                  <tr key={p.patientId}>
                    <td>{p.patientId}</td>
                    <td>
                      {`${p.firstName || ""} ${p.middleName ? p.middleName + " " : ""
                        }${p.lastName || ""}`.trim()}
                    </td>
                    <td>{p.mobile || "-"}</td>
                    <td>
                      <Button
                        className="btn-select-premium"
                        onClick={() => getSelectedPatientFromList(p)}
                      >
                        <FaCheck className="me-1" /> {t("Select")}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
      </Modal>

      {/* Services Modal */}
      <Modal
        show={showServiceModal}
        onHide={() => setShowServiceModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton style={{ backgroundColor: 'var(--primary-color)', color: 'white', border: 'none' }}>
          <Modal.Title className="w-100 text-center text-white">
            <FaList className="me-2" /> {t("Expenses Information")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedServices?.length > 0 ? (
            <div className="table-responsive">
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>{t("Expense Name")}</th>
                    <th>{t("Actual Amount")}</th>
                    <th>{t("Quantity")}</th>
                    <th>{t("Total Amount")}</th>
                    <th>{t("Discount (%)")}</th>
                    <th>{t("Net Amount")}</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedServices.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.serviceDescription}</td>
                      <td>{item.actualAmount}</td>
                      <td>{item.quantity || 0}</td>
                      <td>{item.actualAmount * item.quantity}</td>
                      <td>{item.discount}</td>
                      <td>{item.netAmount}</td>
                    </tr>
                  ))}
                  <tr className="fw-bold bg-light text-end">
                    <td colSpan="5">{t("Total")}</td>
                    <td className="text-start">
                      {selectedServices.reduce(
                        (sum, s) => sum + (Number(s.netAmount) || 0),
                        0
                      ).toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>
          ) : (
            <p>{t("No Expenses available")}</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="btn-secondary-premium px-4"
            onClick={() => setShowServiceModal(false)}
          >
            <FaTimes className="me-2" /> {t("Close")}
          </Button>
        </Modal.Footer>
      </Modal>
      {/* ✅ Update Modal */}
      {/* UPDATE MODAL */}
      <Modal
        show={showUpdateModal}
        onHide={() => setShowUpdateModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton style={{ backgroundColor: 'var(--primary-color)', color: 'white', border: 'none' }}>
          <Modal.Title className="w-100 text-center text-white">
            <FaEdit className="me-2" /> {t("Update Billing Details")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {updateBill && (
            <Form>
              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>{t("Billing Date")}</Form.Label>
                    <Form.Control
                      type="date"
                      value={
                        updateBill.billingDate
                          ? new Date(updateBill.billingDate).toISOString().split("T")[0]
                          : ""
                      }
                      className="bordergrey"
                      onChange={(e) =>
                        setUpdateBill({ ...updateBill, billingDate: e.target.value })
                      }
                    />
                  </Form.Group>
                </div>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-3 p-2 bg-light rounded border">
                <h6 className="fw-bold mb-0 text-teal">
                  <FaMoneyBillWave className="me-2" /> {t("Expense Items")}
                </h6>
                <Button className="btn-premium py-1" onClick={handleAddServiceItem}>
                  <FaPlus className="me-2" /> {t("Add Expense")}
                </Button>
              </div>

              <div className="table-responsive">
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{t("Expense name")}</th>
                      <th>{t("Actual Amount")}</th>
                      <th>{t("Quantity")}</th>
                      <th>{t("Total Amount")}</th>
                      <th>{t("Discount %")}</th>
                      <th>{t("Net Amt")}</th>
                      <th>{t("Action")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {updateBill.serviceItems?.map((item, i) => (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td>
                          <Form.Control
                            value={item.serviceDescription}
                            onChange={(e) =>
                              handleServiceItemChange(i, "serviceDescription", e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <Form.Control
                            type="number"
                            value={item.actualAmount}
                            onChange={(e) =>
                              handleServiceItemChange(i, "actualAmount", e.target.value)
                            }
                            className="bordergrey"
                          />
                        </td>
                        <td>
                          <Form.Control
                            type="number"
                            value={item.quantity}
                            onChange={(e) =>
                              handleServiceItemChange(i, "quantity", e.target.value)
                            }
                            className="bordergrey"
                          />
                        </td>
                        <td>
                          <Form.Control
                            type="number"
                            value={(Number(item.actualAmount) * Number(item.quantity)).toFixed(2)}
                            readOnly
                            className="bordergrey bg-light"
                          />
                        </td>
                        <td>
                          <Form.Control
                            type="number"
                            value={item.discount}
                            onChange={(e) =>
                              handleServiceItemChange(i, "discount", e.target.value)
                            }
                            className="bordergrey"
                          />
                        </td>
                        <td>
                          <Form.Control type="number" value={item.netAmount} readOnly />
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            disabled={updateBill.serviceItems.length === 1}
                            onClick={() => handleDeleteServiceItem(i)}
                          >
                            🗑
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <Row className="mt-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>{t("Total Amount")}</Form.Label>
                    <Form.Control value={updateBill.totalAmount || 0} readOnly className="bordergrey" />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>{t("Amount Received")}</Form.Label>
                    <Form.Control
                      type="number"
                      className="bordergrey"
                      value={updateBill.amountReceived || 0}
                      onChange={(e) => {
                        let v = e.target.value;

                        // Remove leading zeros
                        v = v.replace(/^0+(?=\d)/, "");

                        const r = v === "" ? "" : Number(v);

                        setUpdateBill({
                          ...updateBill,
                          amountReceived: r,
                          outstandingAmount:
                            v === ""
                              ? updateBill.totalAmount
                              : Number((updateBill.totalAmount - r).toFixed(2)),
                        });
                      }}

                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>{t("Outstanding")}</Form.Label>
                    <Form.Control value={updateBill.outstandingAmount || 0} readOnly className="bordergrey" />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button className="btn-secondary-premium px-4" onClick={() => setShowUpdateModal(false)}>
            <FaTimes className="me-2" /> {t("CANCEL")}
          </Button>
          <Button className="btn-premium px-4" onClick={handleSaveUpdateClick} disabled={isLoading}>
            {isLoading ? <Spinner animation="border" size="sm" className="me-2" /> : <FaCheck className="me-2" />}
            {isLoading ? t("UPDATING...") : t("UPDATE BILL")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDeleteBill}
        title={t("Delete Bill")}
        message={t("Are you sure you want to delete this bill?")}
        variant="danger"
      />

      {/* Update Confirmation Modal */}
      <ConfirmModal
        show={showUpdateConfirm}
        onHide={() => setShowUpdateConfirm(false)}
        onConfirm={confirmUpdateBill}
        title={t("Update Bill")}
        message={t("Are you sure you want to save the changes to this bill?")}
        variant="update"
        confirmText={t("Update")}
      />
    </div>
  );
};

export default BillingConfig;
